import * as React from 'react';

import {
  createStyles,
  Grid,
  Theme,
  WithStyles,
  withStyles
} from '@material-ui/core';

import { ILieuucationPropertiesHousing } from '@lieuu/core';

import BarChart from '../charts/BarChart';
import LineYearlyChart from '../charts/LineYearlyChart';
import PieChart from '../charts/PieChart';
import Statistic from '../charts/Statistic';
import StreetViewImage from './StreetViewImage';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
      [theme.breakpoints.up('lg')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6)
      }
    },
    grid: { width: '100%' },
    divider: {
      height: '100%'
    }
  });

type IProps = WithStyles<typeof styles> & {
  properties: ILieuucationPropertiesHousing;
  cbsaProperties: ILieuucationPropertiesHousing;
  centLat: number;
  centLon: number;
};

// many of these descriptions come from
// https://www2.census.gov/programs-surveys/acs/tech_docs/subject_definitions/2018_ACSSubjectDefinitions.pdf

const Housing: React.FC<IProps> = ({
  classes,
  properties,
  cbsaProperties,
  centLon,
  centLat
}) => {
  const homeValueDescription = (
    <>
      {'This is the median from '}
      <b>
        owner-reported estimates of how much their property (house and lot)
        would sell for
      </b>
      {' if it were for sale.'}
      {
        ' It includes only owner-occupied housing units which are one-family houses on less than 10 acres without a business or medical office on the property.'
      }
    </>
  );

  const rentDescription = (
    <>
      {'This is equivalent to the median "gross" rent, which is the '}
      <b>monthly housing cost expenses for renters.</b>
      {
        ' Gross rent is the contract rent plus the estimated average monthly cost of utilities (electricity, gas, and water and sewer) and fuels (oil, wood, etc.) if these are paid by the renter.'
      }
    </>
  );

  return (
    <div className={classes.root}>
      <Grid
        alignItems="center"
        justify="center"
        spacing={2}
        className={classes.grid}
        container
      >
        <Statistic
          title="Home Value"
          variant="dollars"
          value={properties.homeValue.latest}
          cbsaValue={cbsaProperties.homeValue.latest}
          description={homeValueDescription}
        />

        <Statistic
          title="Typical Rent"
          variant="dollars"
          value={properties.grossRent.latest}
          cbsaValue={cbsaProperties.grossRent.latest}
          description={rentDescription}
        />

        <StreetViewImage centLat={centLat} centLon={centLon} />

        <LineYearlyChart
          title="Home Value Trend"
          trendLieuucation={properties.homeValue.trend}
          trendCbsa={cbsaProperties.homeValue.trend}
          description={homeValueDescription}
        />

        <LineYearlyChart
          title="Typical Rent Trend"
          trendLieuucation={properties.grossRent.trend}
          trendCbsa={cbsaProperties.grossRent.trend}
          description={rentDescription}
        />

        <BarChart
          title="Family Households"
          variant="percent"
          data={[
            {
              id: 'Family',
              lieuucation: properties.householdType.family.latest,
              cbsa: cbsaProperties.householdType.family.latest
            },
            {
              id: 'Non-family',
              lieuucation: 1 - properties.householdType.family.latest,
              cbsa: 1 - cbsaProperties.householdType.family.latest
            }
          ]}
          description={
            <>
              {
                'A family consists of a householder and one or more other people living in the same household who are '
              }
              <b>
                {'related to the householder by birth, marriage, or adoption.'}
              </b>
              <br />
              {
                ' A non-family household is defined as a householder living alone or with non-relatives only. '
              }
              <i>
                {
                  'Unmarried couples households, whether opposite-sex or same-sex, with no relatives of the householder that live there are included in non-family households.'
                }
              </i>
            </>
          }
        />

        <BarChart
          title="House Tenure"
          variant="percent"
          data={[
            {
              id: 'Owner',
              lieuucation: properties.tenure.owner.latest,
              cbsa: cbsaProperties.tenure.owner.latest
            },
            {
              id: 'Renter',
              lieuucation: properties.tenure.renter.latest,
              cbsa: cbsaProperties.tenure.renter.latest
            },
            {
              id: 'Vacant',
              lieuucation: properties.tenure.vacant.latest,
              cbsa: cbsaProperties.tenure.vacant.latest
            }
          ]}
          description={
            <>
              {
                'This is the breakdown of current occupants of housing in the Micromarket.'
              }
              <b>
                {
                  ' A housing unit is owner-occupied if the owner or co-owner lives in the unit, even if it is mortgaged or not fully paid for.'
                }
              </b>
              <br />
              {
                ' All occupied housing units which are not owner-occupied are classified as renter-occupied.'
              }
              {' The remaining housing units are considered vacant.'}
            </>
          }
        />

        <LineYearlyChart
          title="Percentage of Renters"
          variant="percent"
          trendLieuucation={properties.tenure.renter.trend}
          trendCbsa={cbsaProperties.tenure.renter.trend}
          description={
            <>
              {'This is the percentage of renters in the neighborhood.'}
              {
                <b>
                  {
                    ' Large spikes may indicate construction of apartment buildings or rental properties.'
                  }
                </b>
              }
            </>
          }
          wide
        />

        {/* <Statistic
          title="Homes Built After 2000"
          variant="percent"
          value={properties.homesBuiltOnOrAfter2000}
          cbsaValue={cbsaProperties.homesBuiltOnOrAfter2000}
          description={
            <>
              {
                'This is the percent of homes which were constructed after the year 2000.'
              }
              <b>
                {
                  ' This is usually used to indicate newer neighborhoods and recently developed homes.'
                }
              </b>
            </>
          }
        />
       <Statistic
          title="Utility Gas"
          variant="percent"
          value={properties.gasHeating}
          cbsaValue={cbsaProperties.gasHeating}
          description={
            <>
              {
                'This is the percent of homes which are heated with utility gas, which 
                is gas piped through underground pipes from a central system to serve the neighborhood.'
              }
              <b>
                {
                  ' This is an important indicator of whether there is utility gas 
                  in the neighborhood, which can also be used for gas stove-tops and other appliances.'
                }
              </b>
            </>
          }
        /> */}

        <PieChart
          title="Home Size"
          data={[
            {
              id: 'One Bedroom',
              value: properties.bedrooms.overall.one
            },
            {
              id: 'Two Bedrooms',
              value: properties.bedrooms.overall.two
            },
            {
              id: 'Three Bedrooms',
              value: properties.bedrooms.overall.three
            },
            {
              id: 'Four Bedrooms',
              value: properties.bedrooms.overall.four
            },
            {
              id: 'Five+ Bedrooms',
              value: properties.bedrooms.overall.fiveOrMore
            }
          ]}
          description={
            <>
              {'This is defined as'}
              <b>
                {
                  ' the number of rooms designed to be used as bedrooms for both occupied and vacant housing units.'
                }
              </b>
            </>
          }
        />

        <PieChart
          title="Household Sizes"
          data={[
            {
              id: 'Single',
              value: properties.householdType.singleHouseholds
            },
            {
              id: 'Two Person',
              value: properties.householdType.twoPersonHouseholds
            },
            {
              id: 'Three+ People',
              value: properties.householdType.twoOrMoreHouseholds
            }
          ]}
          description={
            <>
              {'This is defined as'}
              <b>{' the count of all the people who occupy a housing unit.'}</b>
              {
                ' The occupants may be a single family, one person living alone, two or more families living together, or any other group of related or unrelated people who share living arrangements.'
              }
            </>
          }
        />

        <PieChart
          title="Mortgage Status"
          data={[
            {
              id: 'No Mortgage',
              value: properties.mortgageStatus.none
            },
            {
              id: 'Single Mortgage',
              value: properties.mortgageStatus.mortgage
            },
            {
              id: 'Excess Debt',
              value: properties.mortgageStatus.helocOrSecondMortgage
            }
          ]}
          description={
            <>
              {'This is the mortgage status for owner-occupied housing units. '}
              <i>{'Mortgage'}</i>
              {
                ' refers to all forms of debt where the property is pledged as security for repayment of the debt, including deeds of trust, trust deeds, contracts to purchase, land contracts, junior mortgages, and home equity loans.'
              }
              <b>
                {
                  ' Excess debt is usually a second mortgage or home equity loan.'
                }
              </b>
            </>
          }
        />
      </Grid>
    </div>
  );
};

export default withStyles(styles)(Housing);
